// ══════════════════════════════════════════════════════════════════════════════
//  #FLEET_UNIT_RENAME_V1 (2026-10-02) — carry a Unit # change on the Equipment Master through
//  to everything that is filed under that number.
//
//  WHY: nothing in Fleet has a hidden ID. Daily Inventory, weekly hours, the yard board, service,
//  shop notes, toll/inspection and the PO logs all find a unit by its NUMBER (unitKey — hyphen
//  stripped, "25-0028" → "250028"). So correcting a number on the Master used to change the Master
//  row and nothing else: the unit went blank on Daily, its hours and board tile dropped off, and
//  its PO repair cost showed "—". Caught on the second 120AT (25-0028 → 24-0028, 2026-10-02).
//
//  HOW: one rule per stored key, each one knowing that key's exact shape. Nothing is found by
//  blind search-and-replace — a key with no rule here is left alone and reported back, so free
//  text someone typed (a locator note, a damage report) is never rewritten behind their back.
//
//  Every rule re-reads the stored copy, changes only this unit's entries and writes that back, so
//  it does not carry a stale tab's copy of anything else. If the NEW number already has an entry in
//  a given spot, that spot is left as it is and counted as a clash rather than overwritten.
// ══════════════════════════════════════════════════════════════════════════════
import { unitNorm, unitKey } from './unitValid';

const isObj = (x) => !!x && typeof x === 'object' && !Array.isArray(x);

// obj[from] → obj[to], keeping its place. Same reference back when there is nothing to move.
const moveKey = (obj, x, c) => {
  if (!isObj(obj) || !Object.prototype.hasOwnProperty.call(obj, x.fromKey)) return obj;
  if (Object.prototype.hasOwnProperty.call(obj, x.toKey)) { c.clash++; return obj; }
  const next = {};
  Object.keys(obj).forEach(k => { next[k === x.fromKey ? x.toKey : k] = obj[k]; });
  c.n++;
  return next;
};
// Apply fn to one field of a blob; same reference back when that field did not change.
const sub = (blob, field, fn) => {
  if (!isObj(blob)) return blob;
  const cur = blob[field], nxt = fn(cur);
  return nxt === cur ? blob : { ...blob, [field]: nxt };
};
const mapArr = (arr, fn) => {
  if (!Array.isArray(arr)) return arr;
  let changed = false;
  const out = arr.map(r => { const n = fn(r); if (n !== r) changed = true; return n; });
  return changed ? out : arr;
};
// Rows that carry the number as a `unit` field (shop history, PM flags, toll, inspection, digests).
const unitRows = (arr, x, c, more) => mapArr(arr, r => {
  if (!isObj(r) || unitKey(r.unit) !== x.fromKey) return r;
  if (c) c.n++;
  return { ...r, unit: x.to, ...(more ? more(r) : {}) };
});
// { anything: { UNITKEY: … } } — Daily's days, weekly-hrs' weeks.
const eachChild = (obj, x, c) => {
  if (!isObj(obj)) return obj;
  let changed = false; const out = {};
  Object.keys(obj).forEach(k => { const n = moveKey(obj[k], x, c); if (n !== obj[k]) changed = true; out[k] = n; });
  return changed ? out : obj;
};
const quiet = () => ({ n: 0, clash: 0 });   // for the secondary spots of a store already counted once

const poRows = (blob, x, c) => mapArr(blob, r => {
  if (!isObj(r)) return r;
  const hitU = unitKey(r.unit) === x.fromKey;
  const hitD = r.ducType === 'fleet-unit' && unitKey(r.ducOrUnit) === x.fromKey;
  if (!hitU && !hitD) return r;
  c.n++;
  const next = { ...r, ...(hitU ? { unit: x.to } : {}), ...(hitD ? { ducOrUnit: x.to } : {}) };
  // unitWas remembers the number a PO was first booked under. Going back to that number clears it.
  if (unitKey(r.unitWas) === x.toKey) delete next.unitWas; else if (!r.unitWas) next.unitWas = x.from;
  return next;
});

// [ key pattern, label shown to the user, what one counted item is, transform ]
const RULES = [
  [/^fleet-daily-\d{4}-\d{2}-\d{2}$/, 'Daily Inventory', 'day', (b, x, c) => eachChild(b, x, c)],
  [/^fleet-weekly-hrs$/, 'Weekly hours', 'reading', (b, x, c) => sub(b, 'weeks', w => eachChild(w, x, c))],
  [/^fleet-service$/, 'Service record', 'record', (b, x, c) => sub(b, 'units', u => moveKey(u, x, c))],
  [/^fleet-summary$/, 'Fleet summary', 'entry', (b, x, c) => {
    let n = sub(b, 'state', s => moveKey(s, x, c));
    n = sub(n, 'idle', a => unitRows(a, x)); n = sub(n, 'shop', a => unitRows(a, x));
    return n;
  }],
  [/^fleet-inshop-snapshot$/, 'Fleet summary', 'entry', (b, x, c) => sub(b, 'shop', a => unitRows(a, x, c))],
  [/^fleet-shop-notes$/, 'Shop notes', 'note', (b, x, c) => sub(b, 'notes', o => moveKey(o, x, c))],
  [/^fleet-shop-history$/, 'Shop history', 'visit', (b, x, c) => sub(b, 'records', a => unitRows(a, x, c))],
  [/^fleet-shop-dismissed$/, 'Shop history', 'visit', (b, x) => sub(b, 'dismissed', o => {
    if (!isObj(o)) return o;
    const pre = x.fromKey + '__'; let changed = false; const out = {};
    Object.keys(o).forEach(k => {
      const nk = k.startsWith(pre) ? x.toKey + '__' + k.slice(pre.length) : k;
      if (nk !== k && !Object.prototype.hasOwnProperty.call(o, nk)) { changed = true; out[nk] = o[k]; } else out[k] = o[k];
    });
    return changed ? out : o;
  })],
  [/^fleet-pm-flags$/, 'PM equipment flags', 'flag', (b, x, c) => sub(b, 'items', a => unitRows(a, x, c))],
  [/^fleet-tolltags$/, 'Toll tag', 'tag', (b, x, c) => sub(b, 'items', a => unitRows(a, x, c,
    r => (unitKey(r.unitNum) === x.fromKey ? { unitNum: x.toKey } : {})))],
  [/^fleet-inspections$/, 'Inspection', 'record', (b, x, c) => sub(b, 'items', a => unitRows(a, x, c))],
  [/^fleet-tollinsp-dismissed$/, 'Toll / inspection dismissals', 'dismissal', (b, x, c) =>
    sub(sub(b, 'toll', o => moveKey(o, x, c)), 'insp', o => moveKey(o, x, c))],
  [/^fleet-yard-board$/, 'Yard board', 'tile', (b, x, c) => {
    const q = quiet();
    let n = sub(b, 'positions', o => moveKey(o, x, c));
    n = sub(n, 'labels', o => moveKey(o, x, q));
    n = sub(n, 'tileOrder', o => {
      if (!isObj(o)) return o;
      let changed = false; const out = {};
      Object.keys(o).forEach(l => {
        const a = o[l];
        const na = (Array.isArray(a) && a.includes(x.fromKey) && !a.includes(x.toKey)) ? a.map(t => t === x.fromKey ? x.toKey : t) : a;
        if (na !== a) changed = true; out[l] = na;
      });
      return changed ? out : o;
    });
    c.clash += q.clash;
    return n;
  }],
  [/^po-(log-[a-z0-9]+|hist-bm-(\d{4}|undated))$/, 'PO logs', 'PO', poRows],
];

export const unitRenameRuleFor = (key) => RULES.find(r => r[0].test(String(key || ''))) || null;

// Keys the app may hold for a unit, for when the one-request search is not available. Daily started
// the week of 2026-08-03; one key per week from there through next week.
export const unitRenameFallbackKeys = (now) => {
  const keys = ['fleet-weekly-hrs', 'fleet-service', 'fleet-summary', 'fleet-inshop-snapshot', 'fleet-shop-notes',
    'fleet-shop-history', 'fleet-shop-dismissed', 'fleet-pm-flags', 'fleet-tolltags', 'fleet-inspections',
    'fleet-tollinsp-dismissed', 'fleet-yard-board', 'po-hist-bm-undated'];
  const end = new Date(now || Date.now()); end.setDate(end.getDate() + 7);
  const p2 = (n) => String(n).padStart(2, '0');
  for (let d = new Date(2026, 7, 3); d <= end; d.setDate(d.getDate() + 7)) keys.push(`fleet-daily-${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`);
  for (let y = 2021; y <= end.getFullYear(); y++) keys.push(`po-hist-bm-${y}`);
  ['bm', 'cj', 'ct', 'pg', 'rm', 'gw', 'jk', 'kc', 'cv', 'nv', 'ap'].forEach(cde => keys.push(`po-log-${cde}`));
  return keys;
};

// keys: the stored keys to look at (from the search, or unitRenameFallbackKeys()).
// dryRun: read and count only — used to show what WILL move before anything is written.
// Returns { areas:[{label, n, noun}], clash, changed:[key], leftAlone:[key] }.
export async function renameUnitEverywhere({ sg, ss, from, to, keys, dryRun }) {
  const x = { from: unitNorm(from), to: unitNorm(to), fromKey: unitKey(from), toKey: unitKey(to) };
  const out = { areas: [], clash: 0, changed: [], leftAlone: [] };
  if (!x.fromKey || !x.toKey || x.fromKey === x.toKey) return out;
  const tally = {};
  for (const key of [...new Set(keys || [])]) {
    const rule = unitRenameRuleFor(key);
    if (!rule) { if (!/backup|bak|^fleet-equipment$|^fleet-unit-renames$/i.test(key)) out.leftAlone.push(key); continue; }
    const blob = await sg(key);
    if (blob == null) continue;
    const c = { n: 0, clash: 0 };
    const next = rule[3](blob, x, c);
    out.clash += c.clash;
    if (next === blob) continue;
    if (!dryRun) await ss(key, next);
    out.changed.push(key);
    const t = tally[rule[1]] || (tally[rule[1]] = { label: rule[1], n: 0, noun: rule[2] });
    t.n += c.n;
  }
  out.areas = Object.values(tally).filter(t => t.n > 0);
  return out;
}
